/**
 * Letter Type Icon Mapping
 *
 * Maps HR letter types (display name or template code) to semantic icons.
 * Extend LETTER_TYPE_ICONS and TEMPLATE_CODE_ICONS when new letter types are added.
 */

import {
  FileText,
  Briefcase,
  Plane,
  Globe,
  TrendingUp,
  DollarSign,
  BadgeCheck,
  FileCheck,
  ArrowRightLeft,
  AlertTriangle,
  XCircle,
  Package,
} from 'lucide-react';

/** Display name (lowercase) → Icon. Uses .includes() for flexible matching. */
const LETTER_TYPE_ICONS = {
  'employment verification': Briefcase,
  'home leave': Plane,
  'home leave authorization': Plane,
  'pr application support': Globe,
  'pr support': Globe,
  'promotion': TrendingUp,
  'promotion letter': TrendingUp,
  'increment': DollarSign,
  'increment letter': DollarSign,
  'salary inc': DollarSign,
  'confirmation': BadgeCheck,
  'confirmation letter': BadgeCheck,
  'appointment': FileCheck,
  'appointment letter': FileCheck,
  'transfer': ArrowRightLeft,
  'transfer letter': ArrowRightLeft,
  'warning': AlertTriangle,
  'warning letter': AlertTriangle,
  'termination': XCircle,
  'termination letter': XCircle,
};

/** Template code prefixes (partial match, uppercase). */
const TEMPLATE_CODE_ICONS = [
  { codes: ['PROMO', 'PROM'], icon: TrendingUp },
  { codes: ['INC', 'INCREMENT', 'SALARY_INC', 'PAY_INC'], icon: DollarSign },
  { codes: ['CONFIRM', 'CONFIRMATION'], icon: BadgeCheck },
  { codes: ['APPOINT', 'APPOINTMENT'], icon: FileCheck },
  { codes: ['TRANSFER', 'TRANS'], icon: ArrowRightLeft },
  { codes: ['WARN', 'WARNING', 'DISCIPLINARY', 'DISC'], icon: AlertTriangle },
  { codes: ['TERM', 'TERMINATION'], icon: XCircle },
  { codes: ['EMP_VER', 'EV', 'EMP_VERIF'], icon: Briefcase },
  { codes: ['HOME_LEAVE', 'HLA'], icon: Plane },
  { codes: ['PR_SUPPORT', 'PR_'], icon: Globe },
];

/**
 * Get the semantic icon for an HR letter based on type and template code.
 * @param {Object} letter - Letter object with letter_type_display, template_code, source
 * @returns {React.ComponentType} Lucide icon component
 */
export function getLetterTypeIcon(letter) {
  if (letter?.source === 'asset_ack') return Package;

  const display = (letter?.letter_type_display || '').toLowerCase();
  const code = (letter?.template_code || '').toUpperCase();

  for (const [key, Icon] of Object.entries(LETTER_TYPE_ICONS)) {
    if (display.includes(key)) return Icon;
  }

  for (const { codes, icon } of TEMPLATE_CODE_ICONS) {
    if (codes.some((c) => code.includes(c))) return icon;
  }

  return FileText;
}
